// Memory API client functions

import { getHeaders } from "./api";
import { useLogin, getToken } from "../authConfig";
import { useMsal } from "@azure/msal-react";

const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

// Types
export interface Memory {
    id: string;
    type: 'preference' | 'fact' | 'instruction' | 'context';
    content: string;
    confidence: number;
    created_at: string;
    updated_at: string;
    tags: string[];
    source_conversation_id?: string;
    is_active: boolean;
}

export interface MemorySettings {
    memory_enabled: boolean;
    personalization_enabled: boolean;
    auto_learn_enabled: boolean;
    privacy_level: 'low' | 'medium' | 'high';
    updated_at: string;
}

export interface MemoryStats {
    total_memories: number;
    active_memories: number;
    inactive_memories: number;
    by_type: {
        preference: number;
        fact: number;
        instruction: number;
        context: number;
    };
}

export interface CreateMemoryRequest {
    content: string;
    type: 'preference' | 'fact' | 'instruction' | 'context';
    confidence?: number;
    source_conversation_id?: string;
    source_message_id?: string;
    tags?: string[];
}

export interface UpdateMemoryRequest {
    content?: string;
    type?: 'preference' | 'fact' | 'instruction' | 'context';
    confidence?: number;
    tags?: string[];
    is_active?: boolean;
}

// Helper function to get headers with auth
async function getMemoryApiHeaders(idToken?: string) {
    const authHeaders = await getHeaders(idToken);
    return {
        ...authHeaders,
        'Content-Type': 'application/json'
    };
}

// Memory CRUD operations
export async function getMemories(
    type?: string,
    active: boolean = true,
    limit: number = 100,
    idToken?: string
): Promise<{ memories: Memory[]; total: number }> {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    params.set('active', active.toString());
    params.set('limit', limit.toString());

    const response = await fetch(`${BACKEND_URI}/api/memories?${params.toString()}`, {
        method: 'GET',
        headers: await getMemoryApiHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Failed to get memories: ${response.statusText}`);
    }

    return await response.json();
}

export async function getMemory(id: string, idToken?: string): Promise<Memory> {
    const response = await fetch(`${BACKEND_URI}/api/memories/${id}`, {
        method: 'GET',
        headers: await getMemoryApiHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Failed to get memory: ${response.statusText}`);
    }

    return await response.json();
}

export async function createMemory(memory: CreateMemoryRequest, idToken?: string): Promise<Memory> {
    const response = await fetch(`${BACKEND_URI}/api/memories`, {
        method: 'POST',
        headers: await getMemoryApiHeaders(idToken),
        body: JSON.stringify(memory)
    });

    if (!response.ok) {
        throw new Error(`Failed to create memory: ${response.statusText}`);
    }

    return await response.json();
}

export async function updateMemory(id: string, updates: UpdateMemoryRequest, idToken?: string): Promise<Memory> {
    const response = await fetch(`${BACKEND_URI}/api/memories/${id}`, {
        method: 'PUT',
        headers: await getMemoryApiHeaders(idToken),
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        throw new Error(`Failed to update memory: ${response.statusText}`);
    }

    return await response.json();
}

export async function deleteMemory(id: string, idToken?: string): Promise<void> {
    const response = await fetch(`${BACKEND_URI}/api/memories/${id}`, {
        method: 'DELETE',
        headers: await getMemoryApiHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Failed to delete memory: ${response.statusText}`);
    }
}

export async function bulkDeleteMemories(memoryIds: string[], idToken?: string): Promise<{
    results: Record<string, boolean>;
    deleted: number;
    total: number;
}> {
    const response = await fetch(`${BACKEND_URI}/api/memories/bulk-delete`, {
        method: 'POST',
        headers: await getMemoryApiHeaders(idToken),
        body: JSON.stringify({ memory_ids: memoryIds })
    });

    if (!response.ok) {
        throw new Error(`Failed to bulk delete memories: ${response.statusText}`);
    }

    return await response.json();
}

export async function searchMemories(
    query: string, 
    limit: number = 20,
    idToken?: string
): Promise<{ memories: Memory[]; total: number; query: string }> {
    const response = await fetch(`${BACKEND_URI}/api/memories/search`, {
        method: 'POST',
        headers: await getMemoryApiHeaders(idToken),
        body: JSON.stringify({ query, limit })
    });

    if (!response.ok) {
        throw new Error(`Failed to search memories: ${response.statusText}`);
    }

    return await response.json();
}

// Memory Settings operations
export async function getMemorySettings(idToken?: string): Promise<MemorySettings> {
    const response = await fetch(`${BACKEND_URI}/api/memory-settings`, {
        method: 'GET',
        headers: await getMemoryApiHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Failed to get memory settings: ${response.statusText}`);
    }

    return await response.json();
}

export async function updateMemorySettings(settings: Partial<MemorySettings>, idToken?: string): Promise<MemorySettings> {
    const response = await fetch(`${BACKEND_URI}/api/memory-settings`, {
        method: 'PUT',
        headers: await getMemoryApiHeaders(idToken),
        body: JSON.stringify(settings)
    });

    if (!response.ok) {
        throw new Error(`Failed to update memory settings: ${response.statusText}`);
    }

    return await response.json();
}

// Memory Stats
export async function getMemoryStats(idToken?: string): Promise<MemoryStats> {
    const response = await fetch(`${BACKEND_URI}/api/memories/stats`, {
        method: 'GET',
        headers: await getMemoryApiHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Failed to get memory stats: ${response.statusText}`);
    }

    return await response.json();
}

// Memory API functions for chat integration
export async function getMemoryApi(query: string, idToken?: string): Promise<Memory[]> {
    const response = await fetch(`${BACKEND_URI}/api/memory`, {
        method: 'POST',
        headers: await getMemoryApiHeaders(idToken),
        body: JSON.stringify({ query, limit: 10 })
    });

    if (!response.ok) {
        throw new Error(`Failed to retrieve memories: ${response.statusText}`);
    }

    const result = await response.json();
    return result.memories || [];
}

export async function extractMemoryApi(conversation: string, idToken?: string): Promise<void> {
    const response = await fetch(`${BACKEND_URI}/api/memory/extract`, {
        method: 'POST',
        headers: await getMemoryApiHeaders(idToken),
        body: JSON.stringify({ conversation })
    });

    if (!response.ok) {
        throw new Error(`Failed to extract memories: ${response.statusText}`);
    }
}
